'use memo';

import { useContext, useState } from 'react';
import dayjs from 'dayjs';
import {
  AgxColumn,
  AgxHeader,
  Images,
  AgxBodyText,
  AgxButton,
  AgxSlideUpModal,
  useWindowSize,
  Breakpoints,
  AgxSparkleForm,
  FormConfigType,
} from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { AuthContext } from '@urbanx/agx.auth.client';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { Tabs } from 'types/Tabs';
import { setAndShowErrorToast } from 'store/config';
import useStateFormLabels from '../../../../../form/FormLabels/useStateFormLabels';
import FormDetailsHeader from './FormDetailsHeader';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getEnumValue } from 'helpers/enumHelper';
import './ResumeFormDetails.scss';
import { CampaignDetailModel, CampaignDetailModelStage, CampaignDetailModelState, FormType, InProgressFormModelFormType } from '@urbanx/agx.campaigns.client';

interface ResumeFormDetailsProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ResumeFormDetails = ({
  campaign,
  currentTab,
  mobile = false,
}: ResumeFormDetailsProps) => {
  const { inProgressForms, address, generatedDocuments, campaignId, stage } =
    campaign;

  const disabledStages: CampaignDetailModelStage[] = [
    CampaignDetailModelStage.AgreementAgentSigning,
    CampaignDetailModelStage.AgreementVendorSigning,
  ];

  const availableForms = useAppSelector(state => state.forms.availableForms);

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(() =>
    disabledStages.includes(stage as CampaignDetailModelStage)
  );

  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const formName = useStateFormLabels(campaign.state ?? CampaignDetailModelState.QLD, 'formName');

  const isHaveGeneratedDocs = (generatedDocuments?.length ?? 0) > 0;

  const validInProgressForms = inProgressForms?.filter(ipf => ipf != null);

  const stageEnumValue = getEnumValue(CampaignDetailModelStage, campaign.stage ?? CampaignDetailModelStage.AgreementDraft);

  const inProgressForm = stageEnumValue === CampaignDetailModelStage.AgreementSubmissionIncomplete
    ? validInProgressForms?.find(
          ipf => ipf.formType === InProgressFormModelFormType.ListingSubmission
        )
    : validInProgressForms?.find(
          ipf => ipf.formType === InProgressFormModelFormType.ListingGenerator
        );

  if (!inProgressForm) {
    return <>No in progress forms found</>;
  }

  if (!formName || !availableForms) return;

  const { formId, lastUpdate } = inProgressForm;

  const lastUpdatedOn = `Last updated ${dayjs(lastUpdate).format('DD/MM/YY')}`;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const resumeAgencyAgreement = async () => {
    try {
      setDisableButtons(true);

      await loadForm(campaignId ?? '', formId ?? '', FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Agency Agreement'));
    }

    setDisableButtons(false);
  };

  const beginAgencyAgreementSubmission = async () => {
    const agencyAgreementSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ListingSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (agencyAgreementSubmissionForm == null) {
      return;
    }

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();
      if (!authToken) return;

      const newForm = await completeCampaignStep(
        authToken,
        campaignId ?? '',
        true,
        inProgressForm?.formId
      );

      if (!newForm) {
        dispatch(
          setAndShowErrorToast('Failed to start Agency Agreement submission')
        );
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      dispatch(
        setAndShowErrorToast('Failed to start Agency Agreement submission')
      );
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the {formName} once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          large
          primary
          text="Yes, start submission"
          onClick={beginAgencyAgreementSubmission}
          disabled={
            disableButtons ||
            ((availableForms == null || availableForms.length === 0) &&
              currentTab === Tabs.Archive)
          }
        />
        <AgxButton
          id="cancel"
          hollow
          large
          disabled={disableButtons || currentTab === Tabs.Archive}
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
        />
      </div>
    </AgxColumn>
  );

  return (
    <>
      <FormDetailsHeader
        id={'resume-form-header'}
        formName={formName}
        detail={lastUpdatedOn}
        currentTab={currentTab}
        onBack={undefined}
      />
      <AgxColumn centered veryLargeGap extraClasses="resumeFormDetails">
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        {(stageEnumValue ===
          CampaignDetailModelStage.AgreementSubmissionIncomplete ||
          !isHaveGeneratedDocs) && (
          <AgxColumn smallGap centered>
            <AgxBodyText medium centered>
              There are more fields to fill,
            </AgxBodyText>
            <AgxBodyText medium centered>
              let's continue from where you left off
            </AgxBodyText>
          </AgxColumn>
        )}
        <AgxSparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxColumn>
      <AgxColumn mediumGap>
        <AgxButton
          id="btn-resume"
          primary={mobile || isHaveGeneratedDocs}
          hollow={!isHaveGeneratedDocs && !mobile}
          wide={mobile}
          large
          disabled={disableButtons || currentTab === Tabs.Archive}
          text={`Resume ${
            mobile
              ? ''
              : stageEnumValue ===
                  CampaignDetailModelStage.AgreementSubmissionIncomplete
                ? 'Submission'
                : 'Agreement'
          }`}
          onClick={resumeAgencyAgreement}
          shrinkOnLargerDevices
          extraClasses="resumeFormActionButtonStyle"
        />
        {!validInProgressForms?.some(
          ipf => ipf.formType === InProgressFormModelFormType.ListingSubmission
        ) &&
          !!campaign.agencyAgreementEnvelope &&
          isHaveGeneratedDocs && (
            <AgxButton
              id="btn-startSubmission"
              hollow
              wide={mobile}
              large
              disabled={disableButtons || currentTab === Tabs.Archive}
              text="Start Submission"
              onClick={() => setShowConfirmationModel(true)}
              shrinkOnLargerDevices
              extraClasses="resumeFormActionButtonStyle"
            />
          )}
      </AgxColumn>

      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}
    </>
  );
};

export default ResumeFormDetails;
